// The rules applied to moves, shared by the client's turn controller (GameEngine) and the server's Authority.
// Moves are plain data ({ type, player, ...params }); see Moves/Moves.js for each move's definition.
const GameState = require('./entities/GameState');
const MoveDefinitions = require('./Moves/Moves');
const { SettleBlueBank } = require('./Moves/Decisions');
const { TurnFlags } = require('./Effects/Effects');

const clone = (data) => JSON.parse(JSON.stringify(data));

// Why a move is not legal right now, or null
const Validate = (gameState, move) => {
    const definition = MoveDefinitions[move.type];
    if (definition === undefined)
    {
        return `Unknown move ${move.type}`;
    }
    if (gameState.finished)
    {
        return "The game is over";
    }
    if (gameState.playerStates[move.player]?.resigned)
    {
        return "You have resigned";
    }
    // A decision can be made by its player at any time (see Moves/Decisions.js); everything else is the current player's
    if (definition.anyPlayer)
    {
        return definition.validate(gameState, move);
    }
    if (move.player !== gameState.currentPlayer)
    {
        return "It is not that player's turn";
    }
    // (a phase, or the phases it is allowed in)
    const phases = definition.phase === undefined ? null : [].concat(definition.phase);
    if (phases && !phases.includes(gameState.phase))
    {
        return `Only allowed during the ${phases.map(phase => phase.toLowerCase()).join(' or ')} phase`;
    }
    // The political action given up for International Agreement: the political phase is there only for what comes before it
    if (definition.politicalAction && gameState.playerStates[move.player].turnFlags.includes(TurnFlags.PoliticalGivenUp))
    {
        return "Your political action was given up for International Agreement";
    }
    // Open decisions hold the turn up: the player's own come first, and a blocking one waits for its player
    const waiting = WaitingOn(gameState, move.player);
    if (waiting)
    {
        return waiting;
    }
    MarkDeclined(gameState, move);
    // Only the action that comes with an action card skips spending an action (see PlayActionCard)
    if (move.free)
    {
        return "An action costs an action unless it comes with an action card";
    }
    return definition.validate(gameState, move);
};

// The player's choice for this move not to use a civil action for some actions only (Gutenberg's: skipRestricted), set
// on them while the move is checked and applied (see PlayerState.RestrictedFor)
const MarkDeclined = (gameState, move) => {
    const player = gameState.playerStates[move.player];
    if (player)
    {
        player.restrictedDeclined = !!move.skipRestricted;
    }
};

// Why the player cannot act yet because of open decisions, or null
const WaitingOn = (gameState, playerNumber) => {
    const own = gameState.decisions.find(decision => decision.player === playerNumber);
    if (own)
    {
        return `Decide on ${own.event} first`;
    }
    const blocking = require('./Moves/Decisions').ActiveBlocking(gameState);
    if (blocking)
    {
        return `Waiting for ${gameState.playerStates[blocking.player].name} to decide on ${blocking.event}`;
    }
    return null;
};

// Applies a validated move to a copy and returns a freshly rebuilt state, so card effects are always wired from data
// and the state passed in is left untouched
const Apply = (gameState, move) => {
    const working = new GameState(clone(gameState.toData()));
    MarkDeclined(working, move);
    MarkDeclined(gameState, move);
    MoveDefinitions[move.type].apply(working, move);
    const result = new GameState(clone(working.toData()));
    result.triggerLog = working.triggerLog;
    result.reports = working.reports;
    result.preparedNotes = working.preparedNotes;
    result.reshuffled = working.reshuffled;
    // Blue tokens owed with an empty bank: settled under what caused it (the move itself when nothing else did)
    // (its description's headline: what was done, not what it cost)
    const cause = working.blueCause ?? MoveDefinitions[move.type].describe(gameState, move).split('\n')[0];
    // (but not for players it waits for until the turn is over; those just let go, at its end, are asked now and the turn
    // waits for them)
    const released = (gameState.deferredBlue ?? []).filter(number => !result.deferredBlue.includes(number));
    result.playerStates.filter(player => !result.deferredBlue.includes(player.playerNumber))
        .forEach(player => SettleBlueBank(result, player, released.includes(player.playerNumber) ? 'International Red Cross' : cause, released.includes(player.playerNumber)));
    // A resignation passes the turn on once what it set off is decided
    if (result.resignAdvance !== null && !require('./Moves/Decisions').ActiveBlocking(result))
    {
        const resigned = result.resignAdvance;
        result.resignAdvance = null;
        result.politicalResolving = false;
        MoveDefinitions.StartNextTurn(result, resigned);
        const advanced = new GameState(clone(result.toData()));
        advanced.triggerLog = result.triggerLog;
        advanced.reports = result.reports;
        advanced.reshuffled = result.reshuffled;
        return advanced;
    }
    // A political action is resolved once nothing it asked is still open
    if (result.politicalResolving && !require('./Moves/Decisions').ActiveBlocking(result))
    {
        result.politicalResolving = false;
    }
    if (result.decisions.length !== working.decisions.length)
    {
        // Rebuilt again so the new decision is part of the state as saved
        const settled = new GameState(clone(result.toData()));
        settled.triggerLog = result.triggerLog;
        settled.reports = result.reports;
        settled.reshuffled = result.reshuffled;
        return settled;
    }
    return result;
};

// Applies a validated move and describes it for the move log, a line each: what the move says (the first line, the
// journal's headline), what it cost and did (summarize), and what cards did in reaction, e.g.
// "Built Drama\nSpent 4 resources\nAristotle: +1 science". Returns { after, description }.
const Step = (before, move) => {
    const definition = MoveDefinitions[move.type];
    const said = definition.describe(before, move);
    const after = Apply(before, move);
    const summary = definition.summarize?.(before, after, move);
    // (a move that says nothing itself, e.g. a choice its result tells: just what happened)
    const description = [said, summary, ...after.triggerLog].filter(Boolean).join('\n');
    return { after, description };
};

// Rebuilds the state from data plus moves; throws if any move is illegal at its point in the sequence
const Replay = (gameStateData, moves) => {
    let gameState = new GameState(clone(gameStateData));
    moves.forEach((move, index) => {
        const error = Validate(gameState, move);
        if (error !== null)
        {
            throw new Error(`Move ${index + 1} (${move.type}) is no longer legal: ${error}`);
        }
        gameState = Apply(gameState, move);
    });
    return gameState;
};

// (A move may be irreversible only sometimes: then irreversible(gameState, move) says; without the state, it is)
const IsIrreversible = (move, gameState) => {
    const irreversible = MoveDefinitions[move.type]?.irreversible;
    return typeof irreversible === 'function' ? !gameState || irreversible(gameState, move) : irreversible === true;
};

module.exports = {
    Validate,
    Apply,
    Step,
    Replay,
    IsIrreversible,
    WaitingOn,
};
